<template>
  <div class="gewerbestep4">

    <div class="semanticsection-title2 first primary--text">Vertragsdaten</div>

    <v-row :class="$vuetify.breakpoint.smAndUp ? 'space-between' : ''"
           no-gutters>
      <v-col cols="12"
             sm="6">
        <date-field v-model="versicherungsbeginn"
                    v-validate="{ required: true, date_format: ['DD.MM.YYYY'], date_between: [minVersBeginn, maxVersBeginn, true], excluded: '29.02.2024' }"
                    :error-messages="errors.collect('versicherungsbeginn')"
                    data-vv-as="Versicherungsbeginn"
                    hint="tt.mm.jjjj"
                    class="required"
                    name="versicherungsbeginn"
                    label="Versicherungsbeginn" />

      </v-col>
      <v-col cols="12"
             sm="6">
        <date-field v-model="hauptfaelligkeit"
                    v-validate="{date_format:['DD.MM.'], excluded: '29.02.'}"
                    :error-messages="errors.collect('hauptfaelligkeit')"
                    no-year
                    hint="tt.mm."
                    name="hauptfaelligkeit"
                    label="abweichende Hauptfälligkeit" />
      </v-col>
    </v-row>
    <v-row no-gutters >
      <v-col cols="12"
             sm="6"
             class="pt-1">
        <span class="dmb-form-text">Laufzeit&nbsp;</span>
        <site-tooltip>
          Bei einer Laufzeit von drei Jahren erhält Ihr Kunde einen Beitragsnachlass von 5 %.
        </site-tooltip>
      </v-col>
      <v-col cols="12"
             sm="6" class="fixedwidthcontainer">
        <v-radio-group v-model="laufzeit"
                   row
                   hide-details
                   name="laufzeit"
                   class="pa-0 fixedwidthlabel"
                   @change="updatePrices" >
      <v-radio value="1">
        <template slot="label">
          ein&nbsp;Jahr
        </template>
      </v-radio>        
      <v-radio value="3">
        <template slot="label">
          drei&nbsp;Jahre
        </template>
      </v-radio>  
    </v-radio-group>
       
      </v-col>
    </v-row>

    <div class="semanticsection-title2 primary--text">Vorversicherungen und Leistungsfälle</div>

    <dmb-accordion ref="accordionStep4Vorv"
                   :items="vorversicherungenItems"
                   component-name="GewerbeVorversicherungen"
                   state-path="gewerbe.ui.accordions.step4Vorv" />

    <template v-if="immobilien.length > 0">
      <div class="semanticsection-title2 primary--text">Gewerblicher Immobilien-Rechtsschutz</div>
      <gewerbe-immobilien :immobilien="immobilienItems"></gewerbe-immobilien>
    </template>


    <template v-if="isKompex && !excludeGf && ferienwohnungen.length > 0">
      <div class="semanticsection-title2 primary--text">Adressdaten Zweit-/Ferienwohnung</div>

      <dmb-accordion ref="accordionStep4Zweit"
                     :items="ferienwohnungenItems"
                     component-name="AddressData"
                     state-path="gewerbe.ui.accordions.step4Zweit" />
    </template>

    <template v-if="isKompex && !excludeGf && wohnungen.length > 0">
      <div class="semanticsection-title2 primary--text">Adressdaten privat vermietete Wohneinheiten</div>

      <dmb-accordion ref="accordionStep4Wohn"
                     :items="wohnungenItems"
                     component-name="GewerbeImmobilien"
                     state-path="gewerbe.ui.accordions.step4Wohn" />
    </template>

    <div class="semanticsection-title2 primary--text">Zusätzliche Mitteilungen</div>

    <v-text-field v-model="anmerkungen"
                  v-dmb-trim
                  :counter="200"
                  maxlength="200"
                  clearable
                  class="mitteilungen ma-0" />

    <div class="semanticsection-title2 primary--text">Zusätzliche Dokumente</div>

    <file-upload-multi state-path="gewerbe"></file-upload-multi>

    <template v-if="needsMaknummer">
      <div class="semanticsection-title2 primary--text">Maklernummer</div>
      <v-text-field v-model="maknummer"
                    v-validate="'required'"
                    v-dmb-trim
                    :error-messages="errors.collect('maknummer')"
                    data-vv-as="Zusätzliche Makleridentifikationsnummer"
                    class="required"
                    name="maknummer"
                    label="Zusätzliche Makleridentifikationsnummer" />
    </template>

    <v-row :no-gutters="false" class="stepperbuttons">
      <v-btn color="primary"
             class="standard-button"
             @click="$emit('step-back')">Zurück</v-btn>
      <v-spacer/>
      <v-btn color="primary"
             class="standard-button"
             @click="$emit('step-forward')">Weiter</v-btn>
    </v-row>

  </div>
</template>

<script>
import twowayBindingMixin from '../../store/twowayBindingMixin';
import {mapTwoWay} from '../../store/twowayBindingMixin';
import ValidationMixin from '../helper/ValidationMixin';
import DateField from "../DateField";
import {getDateString} from "@/helper/dateService";
import DmbAccordion from './DmbAccordion';
import { mapGetters, mapState } from 'vuex';
import GewerbeImmobilien from './GewerbeImmobilien';
import {RIGHTS} from "@/store/user";
import FileUploadMulti from '../FileUploadMulti';

export default {
  name: "GewerbeStep4",
  components: { FileUploadMulti, GewerbeImmobilien, DmbAccordion, DateField },
  mixins: [ twowayBindingMixin, ValidationMixin ],
  computed: {
    ...mapTwoWay([
      'versicherungsbeginn',
      'hauptfaelligkeit',
      'maknummer',
      'laufzeit'
    ], {}, 'gewerbe.vertrag'),
    ...mapTwoWay([
      'anmerkungen'
    ], {}, 'gewerbe'),
    ...mapGetters([
      'versicherungsnehmer',
      'mvUnternehmen',
      'geschaeftsfuehrer',
      'vorversicherungPath',
      'wohnungPath',
      'immobiliePath'
    ]),
    ...mapState({
      isKompex: (state) => state.gewerbe.ui.isKompex,
      excludeGf: (state) => state.gewerbe.ui.excludeGF,
      hasMvUnternehmen: (state) => state.gewerbe.ui.hasMvUnternehmen,
      personen: (state) => state.gewerbe.personen,
      vorversicherungen: (state) => state.gewerbe.vorversicherungen,
      immobilien: (state) => state.gewerbe.immobilien,
      ferienwohnungen: (state) => state.gewerbe.ferienwohnungen,
      wohnungen: (state) => state.gewerbe.wohnungen
    }),
    minVersBeginn() {
      return getDateString(new Date(new Date().setDate(new Date().getDate() + 1)));
    },
    maxVersBeginn() {
      const now = new Date();
      const nowPlusOneYear = new Date(now.setFullYear(now.getFullYear() + 1));
      return getDateString(nowPlusOneYear);
    },
    needsMaknummer() {
      return this.$store.getters.checkAuthorization(RIGHTS.MAKLERNUMMER);
    },
    vorversicherungenItems() {
      let pers = [{
        key: this.versicherungsnehmer.key,
        label: `Versichertes Unternehmen`,
        name: `${this.versicherungsnehmer.name} ${this.versicherungsnehmer.rechtsform}`,
        statePath: this.vorversicherungPath(this.versicherungsnehmer.key),
        childProps: {
          label: 'versicherte Unternehmen',
          gegen: 'den Antragsteller/das Unternehmen',
          article: 'das',
          personRef: this.versicherungsnehmer.key
        }
      }];
      if (this.hasMvUnternehmen) pers = pers.concat(this.mvUnternehmen.map((mvu, i) => {
        return {
          key: mvu.key,
          label: `${i+1}. Mitversichertes Unternehmen`,
          name: mvu.name,
          statePath: this.vorversicherungPath(mvu.key),
          childProps: {
            label: 'mitversicherte Unternehmen',
            gegen: 'das mitversicherte Unternehmen',
            article: 'das',
            personRef: mvu.key
          }
        };
      }));
      if (this.isKompex && !this.excludeGf) this.geschaeftsfuehrer.forEach((gf, i) => {
        pers.push({
          key: gf.key,
          label: `${i+1}. Inhaber/Geschäftsführer`,
          name: `${gf.firstname} ${gf.lastname}`,
          statePath: this.vorversicherungPath(gf.key),
          childProps: {
            label: 'Inhaber/Geschäftsführer',
            gegen: 'den Inhaber/Geschäftsführer',
            article: 'den',
            personRef: gf.key
          }
        });
        if (this.$store.state.gewerbe.ui.hasMvPerson.indexOf(gf.key) > -1) {
          const mvp = this.personen.find(p => /^mvp[0-9]*$/.test(p.key) && p.ref === gf.key);
          if (mvp) pers.push({
            key: mvp.key,
            label: `Mitversicherte Person`,
            name: `${mvp.firstname} ${mvp.lastname}`,
            statePath: this.vorversicherungPath(mvp.key),
            childProps: {
              label: 'mitversicherte Person',
              gegen: 'die mitversicherte Person',
              article: 'die',
              personRef: mvp.key,
            }
          });
        }
      });
      return pers.filter(p => this.vorversicherungen.find(v => v.ref === p.key));
    },
    ferienwohnungenItems() {
      return this.ferienwohnungen.map((zw, i) => {
        const p = this.personen.find(p => p.key === zw.ref);
        return {
          key: zw.key,
          label: `${i+1}. Inhaber/Geschäftsführer`,
          name: `${p.firstname} ${p.lastname}`,
          statePath: this.wohnungPath(zw.key, 'ferienwohnungen'),
          childProps: {
            zweitwohnung: true
          }
        };
      });
    },
    wohnungenItems() {
      return this.geschaeftsfuehrer.map((gf, i) => {
        return {
          key: `wohneinheiten_${gf.key}`,
          label: `${i+1}. Inhaber/Geschäftsführer`,
          name: `${gf.firstname} ${gf.lastname}`,
          childProps: {
            staatDisabled: true,
            isWohnung: true,
            immobilien: this.wohnungen.filter(w => w.ref === gf.key).map(w => { return {
                key: w.key,
                bruttojahresmiete: w.bruttojahresmiete,
                statePath: this.wohnungPath(w.key, 'wohnungen')
              };
            })
          }
        };
      }).filter(item => item.childProps.immobilien.length > 0);
    },
    immobilienItems() {
      return this.immobilien.map(i => {
        i.statePath = this.immobiliePath(i.key);
        return i;
      });
    }
  },
  methods: {
      updatePrices() {
        this.$store.dispatch('gewerbeUpdateMatrixDeckung', this.$store.state.gewerbe.ui.matrixSelection);
      }
  }
};
</script>
<style>
.gewerbestep4 .pb-56 {
  padding-bottom: 56px;
}

.gewerbestep4 .min-height {
  min-height: 1.5em;
}

.gewerbestep4 .fixedwidthcontainer{
  justify-content: flex-start !important;
  padding-left: 60px!important;
}

.gewerbestep4 .fixedwidthlabel .v-radio:first-child label{
  width: 90px !important;
}

.gewerbestep4 .fixedwidthlabel .v-input--radio-group__input .v-radio:not(:last-child) {
    margin-right: 0px!important;
}
</style>
